import type { FC, ReactNode } from 'react';
import React, { useContext } from 'react';

import { flex } from 'src/styling/constants';
import { TranslatedStaticText } from 'src/translators/static-text-translator';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import mq from 'src/styling/media-queries';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import { MOST_RECENTLY_VIEW_SEARCH_CLUB_ID } from 'src/components/layout/redesign/header-shared/promo-helper';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import ClubContentProvider from 'src/content/content-providers';
import { ContentAndMetaConsumer } from 'src/content';
import { trimLocationString } from 'src/helpers/string-helper';
import { GlobalCmsContent } from 'src/content/global-content';
import { Link } from 'gatsby';
import { getAbsoluteClubPagePath, getClubPagePath } from 'src/helpers/navigation-helper';
import { getImageForClubFinder } from 'src/helpers/hosted-asset-helpers';
import { getBuildDomainForBuildType } from 'src/enums/build/build-domain';

const createStyling = (styling: Styling) => ({
  mainTitleStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      color: styling.colors.capeCod,
      padding: '0.75rem 0',
      margin: ['2.5rem 0 1.5rem', '2rem 0 0.75rem'],
      borderBottom: `1px solid ${styling.colors.grayOlive}`,
    },
  ]),
  containerStyling: mq({
    padding: 0,
  }),
  wrapperStyling: mq([
    flex.row,
    {
      'padding': '0.75rem 0',
      '&:hover': {
        '.club-name': {
          color: styling.colors.camelot,
          textDecoration: 'underline',
        },
      },
    },
  ]),
  imageStyling: mq({
    minWidth: '5rem',
    width: '5rem',
    height: '5rem',
    objectFit: 'cover',
    borderRadius: '0.29088rem',
  }),
  titleStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1.5rem',
      textTransform: 'capitalize',
      lineHeight: '145%',
      color: styling.colors.capeCod,
    },
  ]),
  comingSoonStyling: mq([
    styling.fonts.bold,
    {
      color: styling.colors.camelot,
      marginLeft: '0.75rem',
      fontSize: '1.1875rem',
    },
  ]),
  addressStyling: mq([
    styling.fonts.regular,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      color: styling.colors.capeCod,
      marginBottom: '0.15rem',
    },
  ]),
  linkStyle: mq([
    styling.common.link,
    {
      fontSize: '1rem',
      lineHeight: '145%',
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      textDecorationSkipLnk: 'none',
      textDecorationThickness: 'auto',
      textUnderlineOffset: '35%',
      textUnderlinePosition: 'from-font',
    },
  ]),
});

export const RecentlyVisitedClubInfo: FC = () => {
  const { styling, buildType, language } = useContext(SiteContext);
  const { headerContent } = useContext(GlobalCmsContent);
  const componentStyle = createStyling(styling);
  const clubId = getLocalStorageItem(MOST_RECENTLY_VIEW_SEARCH_CLUB_ID)!;
  const clubName = getClubInformation(clubId).clubName;
  const clubInformation = getClubInformation(clubId);

  const LinkComponent: FC<{ children?: ReactNode | undefined }> = ({ children }) => {
    return !!clubInformation &&
      getBuildDomainForBuildType(clubInformation.buildType) ===
        getBuildDomainForBuildType(buildType) ? (
      <Link to={getClubPagePath('', clubId, language)}>{children}</Link>
    ) : (
      <a href={getAbsoluteClubPagePath('', clubId, language)}>{children}</a>
    );
  };

  return (
    <ClubContentProvider clubId={clubId}>
      <ContentAndMetaConsumer>
        {(cmsData) => {
          const shared = cmsData.newSharedContent;
          const phone = shared.phoneNumbers.nonMembersPhoneNumber;
          return (
            <div css={componentStyle.containerStyling}>
              <div css={componentStyle.mainTitleStyling}>
                {headerContent.searchSettings.recentlyViewedClubCaption}
              </div>
              <div css={componentStyle.wrapperStyling}>
                <LinkComponent>
                  <img
                    src={getImageForClubFinder(shared.clubThumbnailImage)}
                    alt={clubName}
                    css={componentStyle.imageStyling}
                  />
                </LinkComponent>
                <div css={{ marginLeft: '0.75rem', marginTop: '-0.4rem' }}>
                  <LinkComponent>
                    <span className="club-name" css={componentStyle.titleStyling}>
                      {clubName}
                      {cmsData.sharedContent.comingSoonClubFinder && (
                        <span css={componentStyle.comingSoonStyling}>
                          <TranslatedStaticText lookupKey={'comingSoon'} />
                        </span>
                      )}
                    </span>
                    <div css={componentStyle.addressStyling}>
                      {trimLocationString(shared.clubLocation.text1!, shared.clubLocation.text2!) ??
                        ''}
                    </div>
                  </LinkComponent>
                  <a
                    css={componentStyle.linkStyle}
                    href={`tel:${phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {phone}
                  </a>
                </div>
              </div>
            </div>
          );
        }}
      </ContentAndMetaConsumer>
    </ClubContentProvider>
  );
};
